import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ProgressBar } from "@carbon/react";
import { Add, AiGenerate, TrashCan } from "@carbon/icons-react";
import { useAssignedCategories } from "../../hooks/useAISupplierProfile";
import { useRemoveUserMaterialCategory } from "../../hooks/useUserMaterialCategories";
import { Loader, NetworkError } from "..";
import CategoryPickerModal from "../CategoryPickerModal/CategoryPickerModal";
import ConfirmDialog from "../carbon/ConfirmDialog/ConfirmDialog";
import EmptyState from "../carbon/EmptyState/EmptyState";
import { useLanguage } from "../../hooks";
import { useNotification } from "../../context/hooks";
import { useUser } from "../../context/hooks/useUser";
import { useCompanyProfile } from "../../hooks/useCompany";
import { FileType } from "../../types";
import "./assignedCategories.scss";

interface RemovingCategory {
  id: string;
  name: string;
}

/**
 * Rebuilt on Carbon (Task 5, Opportunity Discovery + AI Profiling flow — the final task of this
 * flow). Same five render states as the pre-Carbon widget (loading / error / AI-processing /
 * empty / populated), same data source (`useAssignedCategories` →
 * `GET /api/supplier/ai-profile/assigned-categories`) and same single mutation
 * (`useRemoveUserMaterialCategory` → `DELETE /userMaterialCategory/{id}`, called with just the
 * `categoryId` string — no request body, byte-identical to before).
 *
 * NOTE on scope: the brief for this task describes an "AI-suggested-category list + manual
 * category picker" and `POST /api/supplier/ai-profile/{assign-categories,assign-manual-categories}`
 * mutations to preserve. Neither exists in this component (or anywhere reachable from
 * `/dashboard`) on this branch today — `useAISupplierProfile.ts` only exports
 * `useAssignedCategories`, and `e2e/opportunities.spec.ts`'s own file header independently
 * confirms there is no analyze/suggested-category button in the component today. The widget does
 * exactly one thing: render the assigned list + remove. (The unused `aiProfile.analysis.*` i18n
 * keys and the `useSuggestedCategories`/`getSuggestedCategories` scaffolding were retired in the
 * 2026-09-16 post-migration cleanup sprint.)
 */
const AssignedCategories = () => {
  const { t } = useTranslation();
  const langEs = useLanguage();
  const {
    data: assignedCategories,
    isLoading,
    error,
  } = useAssignedCategories();
  const [removingCategory, setRemovingCategory] =
    useState<RemovingCategory | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const assignedCategoryIds = useMemo(
    () => new Set((assignedCategories ?? []).map((c) => c.categoryId)),
    [assignedCategories]
  );

  const picker = (
    <CategoryPickerModal
      open={pickerOpen}
      onClose={() => setPickerOpen(false)}
      assignedCategoryIds={assignedCategoryIds}
    />
  );

  const addButton = (
    <Button
      kind="tertiary"
      size="sm"
      renderIcon={Add}
      onClick={() => setPickerOpen(true)}
    >
      {t("aiProfile.picker.addButton")}
    </Button>
  );

  // Get company profile to check if AI should be processing
  const { user } = useUser();
  const { data: companyProfile } = useCompanyProfile(user?.company?.id || "");

  const hasWebsite = !!companyProfile?.website;
  const hasProductCatalog = companyProfile?.files?.some(
    (file: any) => file.type === FileType.PRODUCT_CATALOG
  );
  const shouldShowProcessingMessage =
    (hasWebsite || hasProductCatalog) &&
    (!assignedCategories || assignedCategories.length === 0);

  const { openNotification } = useNotification();
  const { mutate: removeCategory, isPending: isRemoving } =
    useRemoveUserMaterialCategory(
      () => {
        openNotification(t("aiProfile.assigned.successMessage"));
        setRemovingCategory(null);
      },
      () => {
        openNotification(t("aiProfile.assigned.errorMessage"));
        setRemovingCategory(null);
      }
    );

  const handleRemoveCategory = (categoryId: string) => {
    const category = assignedCategories?.find(
      (cat) => cat.categoryId === categoryId
    );
    setRemovingCategory({
      name: category
        ? langEs
          ? category.categoryName
          : category.categoryNameEn
        : "this category",
      id: categoryId,
    });
  };

  if (isLoading) {
    return (
      <div className="assigned-categories assigned-categories--loading">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="assigned-categories">
        <NetworkError
          title={t("aiProfile.assigned.error")}
          message={t("aiProfile.assigned.errorDescription")}
        />
      </div>
    );
  }

  if (!assignedCategories || assignedCategories.length === 0) {
    if (shouldShowProcessingMessage) {
      return (
        <div className="assigned-categories">
          <h3 className="assigned-categories__title">
            {t("aiProfile.assigned.title")}
          </h3>
          <div className="assigned-categories__processing">
            <AiGenerate
              size={24}
              className="assigned-categories__processing-icon"
            />
            <div className="assigned-categories__processing-content">
              <h4 className="assigned-categories__processing-title">
                {t("aiProfile.assigned.processing.title")}
              </h4>
              <p className="assigned-categories__processing-description">
                {t("aiProfile.assigned.processing.description")}
              </p>
              <ProgressBar
                label={t("aiProfile.assigned.processing.status")}
                helperText={t("aiProfile.assigned.processing.progress")}
                value={65}
                max={100}
                status="active"
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="assigned-categories">
        <EmptyState
          title={t("aiProfile.assigned.noCategories")}
          description={t("aiProfile.assigned.noCategoriesDescription")}
        />
        <div className="assigned-categories__empty-action">{addButton}</div>
        {picker}
      </div>
    );
  }

  return (
    <>
      <div className="assigned-categories">
        <div className="assigned-categories__header">
          <h3 className="assigned-categories__title">
            {t("aiProfile.assigned.title")}
          </h3>
          {addButton}
        </div>

        <ul className="assigned-categories__list">
          {assignedCategories.map((assignment) => (
            <AssignedCategoryRow
              key={assignment.assignmentId}
              name={langEs ? assignment.categoryName : assignment.categoryNameEn}
              isRemoving={isRemoving}
              onRemove={() => handleRemoveCategory(assignment.categoryId)}
            />
          ))}
        </ul>
      </div>

      <ConfirmDialog
        open={removingCategory !== null}
        danger
        title={t("aiProfile.assigned.title")}
        body={
          removingCategory
            ? t("aiProfile.assigned.confirmRemove", {
                categoryName: removingCategory.name,
              })
            : ""
        }
        confirmLabel={t("cta.remove")}
        onConfirm={() =>
          removingCategory && removeCategory(removingCategory.id)
        }
        onCancel={() => setRemovingCategory(null)}
        isPending={isRemoving}
      />

      {picker}
    </>
  );
};

interface AssignedCategoryRowProps {
  name: string;
  isRemoving: boolean;
  onRemove: () => void;
}

/**
 * `<li>` inside `assigned-categories__list` — the e2e-pinned row. Pre-Carbon, this row was
 * structurally located by its Tailwind class (its remove `<button>` had no accessible name at
 * all). Both gaps are closed here: the row is now findable via `getByRole("listitem")` +
 * `hasText`, and the remove control is a Carbon icon-only `Button` whose `iconDescription` gives
 * it a real accessible name (`"Remove " + categoryName`, reusing the existing `cta.remove` key —
 * same `hasIconOnly`/`iconDescription` pattern `NewRequest/ItemRow.tsx` already uses for its own
 * row actions), so `getByRole("button", { name: ... })` resolves it directly.
 */
const AssignedCategoryRow = ({
  name,
  isRemoving,
  onRemove,
}: AssignedCategoryRowProps) => {
  const { t } = useTranslation();

  return (
    <li className="assigned-categories__row">
      <span className="assigned-categories__row-name">{name}</span>
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        renderIcon={TrashCan}
        iconDescription={`${t("cta.remove")} ${name}`}
        tooltipPosition="left"
        disabled={isRemoving}
        onClick={onRemove}
      />
    </li>
  );
};

export default AssignedCategories;
